import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Form } from "antd"
import PropTypes from "prop-types"
import { useSelector } from "react-redux"
import { modelSelector } from "store/selectors"
import { WizardContext } from "./WizardContext"
import { buildDefaultValues } from "./mappers/buildDefaultValues"
import { deriveCompletedSteps } from "./mappers/deriveCompletedSteps"
import "ui/Form/Form.css"

const renderRequiredMark = (label, { required }) => (
  <>
    {label}
    {required && " *"}
  </>
)

// Steps completed during the "new" trip phase are stored here until the real ID is known.
// On the new→real ID transition (tripId prop changes, no remount), we migrate this to the real key.
const PENDING_KEY = "wizard-completed-pending"
const ownStorageKey = (id) => (id && id !== "new" ? `wizard-completed-${id}` : null)

// Persisted completion ticks include steps inferred by deriveCompletedSteps. When that
// inference changes (a step that used to count no longer does), previously stored ticks
// would otherwise linger — sessionStorage is only ever unioned, never pruned. Bump this
// version on any such change to invalidate stale entries once per session.
const STORAGE_VERSION = "2"
const VERSION_KEY = "wizard-completed-version"

const purgeStaleCompletionStorage = () => {
  try {
    if (sessionStorage.getItem(VERSION_KEY) === STORAGE_VERSION) return
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index)
      if (key?.startsWith("wizard-completed-")) sessionStorage.removeItem(key)
    }
    sessionStorage.setItem(VERSION_KEY, STORAGE_VERSION)
  } catch { /* ignore storage access errors */ }
}
purgeStaleCompletionStorage()

/**
 * Single source of truth for the experience creation wizard.
 *
 * Provides via WizardContext:
 *   form              — single AntD Form instance shared across all steps
 *   mediaFilesRef     — File objects for the media step (not serializable, never in Redux)
 *   stepSaveHandlersRef — steps with non-form local state register async save functions here
 *   trip              — current server state from Redux
 *
 * Form values are initialized once when the trip loads.
 * AntD's preserve=true (default) keeps field values when step components unmount.
 *
 * component="div" (not component={false}) so AntD renders:
 *   <div class="ant-form ant-form-vertical custom-form">
 * This makes all AntD form CSS (.ant-form-vertical, etc.) apply to descendant
 * Form.Items, matching the styling of the old CustomForm-based wizard.
 */
export const WizardProvider = ({ tripId, children }) => {
  const [form] = Form.useForm()
  const [isSaving, setIsSaving] = useState(false)
  const [isFormInitialized, setIsFormInitialized] = useState(false)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const resetFormDirty = useCallback(() => setIsFormDirty(false), [])

  // On initial mount (page load / refresh): seed completedSteps from sessionStorage.
  // The new→real ID mid-session transition is handled by the useEffect below.
  const [completedSteps, setCompletedSteps] = useState(() => {
    const key = ownStorageKey(tripId)
    if (!key) return new Set() // tripId === "new": always start fresh
    try {
      const stored = sessionStorage.getItem(key)
      return stored ? new Set(JSON.parse(stored)) : new Set()
    } catch {
      return new Set()
    }
  })
  const mediaFilesRef = useRef([])
  const stepSaveHandlersRef = useRef({})
  const stepValidateHandlersRef = useRef({})
  const lastSavedPayloadsRef = useRef({})
  const hasInitialized = useRef(false)

  const markStepCompleted = useCallback((stepId) => {
    setCompletedSteps((prev) => {
      if (prev.has(stepId)) return prev
      const next = new Set(prev)
      next.add(stepId)
      // Write to own key for real trips; pending key for new trips so the data
      // survives the new→real ID transition (handled in the useEffect below).
      const key = ownStorageKey(tripId) ?? PENDING_KEY
      try { sessionStorage.setItem(key, JSON.stringify([...next])) } catch { /* ignore quota errors */ }
      return next
    })
  }, [tripId])

  const trip = useSelector(modelSelector("trip"))
  const availableCategories = useSelector((state) => state.trips.categories.data?.categories || [])

  useEffect(() => {
    if (trip?.id && trip.id === Number(tripId) && !hasInitialized.current) {
      form.setFieldsValue(buildDefaultValues(trip, { availableCategories }))
      hasInitialized.current = true
      setIsFormInitialized(true)

      // Seed ticks from the saved trip so steps completed elsewhere (mobile app, another
      // browser/device) show as done — sessionStorage only knows about this session.
      // Union with whatever's already ticked so in-session progress isn't dropped.
      const derived = deriveCompletedSteps(trip)
      if (derived.size > 0) {
        setCompletedSteps((prev) => {
          const next = new Set(prev)
          let changed = false
          derived.forEach((stepId) => {
            if (!next.has(stepId)) { next.add(stepId); changed = true }
          })
          if (!changed) return prev
          const key = ownStorageKey(tripId)
          if (key) {
            try { sessionStorage.setItem(key, JSON.stringify([...next])) } catch { /* ignore quota errors */ }
          }
          return next
        })
      }
    }
  }, [trip, tripId, form, availableCategories])

  // Fires when tripId prop changes (no remount — same component, different route param).
  // Key case: new→real ID transition after createTrip. The "new" phase wrote completed
  // steps to PENDING_KEY; migrate them to the real key so ticks aren't lost.
  useEffect(() => {
    hasInitialized.current = false
    setIsFormInitialized(false)

    const key = ownStorageKey(tripId)

    if (!key) {
      // Starting a fresh new experience — wipe any stale pending data.
      try { sessionStorage.removeItem(PENDING_KEY) } catch { /* ignore */ }
      setCompletedSteps(new Set())
      return
    }

    try {
      const ownStored = sessionStorage.getItem(key)
      if (ownStored) {
        setCompletedSteps(new Set(JSON.parse(ownStored)))
        return
      }
      // No own data yet — check for steps accumulated during the "new" phase.
      const pendingStored = sessionStorage.getItem(PENDING_KEY)
      if (pendingStored) {
        sessionStorage.setItem(key, pendingStored)
        sessionStorage.removeItem(PENDING_KEY)
        setCompletedSteps(new Set(JSON.parse(pendingStored)))
        return
      }
      setCompletedSteps(new Set())
    } catch {
      setCompletedSteps(new Set())
    }
  }, [tripId])

  const contextValue = useMemo(
    () => ({ form, mediaFilesRef, stepSaveHandlersRef, stepValidateHandlersRef, lastSavedPayloadsRef, trip, isSaving, setIsSaving, isFormInitialized, completedSteps, markStepCompleted, isFormDirty, resetFormDirty }),
    [form, trip, isSaving, setIsSaving, isFormInitialized, completedSteps, markStepCompleted, isFormDirty, resetFormDirty]
  )

  return (
    <WizardContext.Provider value={contextValue}>
      <Form
        form={form}
        layout="vertical"
        component="div"
        className="custom-form"
        requiredMark={renderRequiredMark}
        onValuesChange={() => setIsFormDirty(true)}
        initialValues={{
          isSeasonalExperience: false,
          timeBlocks: [],
          availability_schedules: [],
          schedule_overrides: [],
        }}
      >
        {children}
      </Form>
    </WizardContext.Provider>
  )
}

WizardProvider.propTypes = {
  tripId: PropTypes.string,
  children: PropTypes.node.isRequired,
}
